import { BOOT_LOCALE, LL } from 'i18n/i18n.service';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { parsePrice } from '@cf-shared/shared-util';
import { getMyApp } from 'app/app-interface';
import { NavController } from 'providers/nav-controller';
import { LoggingService } from 'services/logging.service';
import { SettingsService } from 'services/settings.service';
import { SubscriptionStatus } from '@cf-shared/data-model/couch-user';
import { GetSubscriptionInfoResult } from '@cf-shared/google-play-interface';
import { ILoginPage } from 'pages/login/login-interface';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { PaddleCheckoutService, PaddlePlanPrice } from 'services/paddle-checkout.service';
import { UrlQueryService } from 'services/url-query-service';
import { PaddleCheckoutConfig } from '@cf-shared/store-interface';
import { PaddleEventData } from '@paddle/paddle-js';
import { PlanOption } from '../../services/paypro-price-fetcher.service';
import { kAlreadySignedInWaitBudget, kPurchaseFirstWaitBudget, pollForPostPurchaseAsync, totalWaitMs } from './post-purchase-wait-policy';
import { CheckoutEmailVerdict } from './checkout-email-verdict';
import { markAfterPurchaseReload } from 'app/after-purchase-reload';
import { computeYearlySaving } from './yearly-saving';
import {
  dispatchPaddleCheckoutEvent,
  kPaddleCheckoutFailedNoFieldsReport,
  kPaddleCheckoutNetworkErrorReport,
  kPaddleFrameClass,
  openWhenPaddleFrameReady,
  PaddleCheckoutErrorKind,
  PaddleCheckoutPage,
  paddleCheckoutErrorContext,
} from './paddle-inline-frame';

/** How long the purchase-first reload waits for the buyer's new local store to open (#6788).
 *  Well beyond a healthy open, which takes under a second, and short enough that a slow
 *  phone barely notices. */
const kPurchaseReloadStoreWaitMs = 5_000;

/**
 * How the purchase-first wait stopped, when it stopped for a reason other than running
 * out of attempts. Both endings drop the buyer into the same "registration delayed"
 * alert, so the timeout report has to say which one happened.
 */
enum PurchaseFirstSignInEnding {
  /** The claim token was exchanged and the buyer now has a session. */
  SignedIn = 'signed-in',
  /** A token was minted but exchanging it failed — re-fetching the same one would not help. */
  ExchangeFailed = 'exchange-failed',
}

/** What the rail borrows from the page it is rendering inside. */
export interface PaddleRailHost {
  /**
   * True once the page's view has been torn down (the NG0911 guard).
   *
   * A method rather than a property because the answer changes while the rail is
   * mid-await, and because the page supplies these as arrow functions closing
   * over itself.
   */
  isViewGone(): boolean;
  /** Mark the page dirty — the app is zoneless, so a model change alone repaints nothing. */
  markPageForCheck(): void;
  /** The page's `safeTimeout`: cancelled on teardown, and skipped once the view is gone. */
  scheduleOnPage(callback: () => void, delayMs: number): void;
  /** Show or hide the page's checkout spinner. */
  setLoadingCheckout(loading: boolean): void;
  /** Replace the plan list the page's shared plan selector renders. */
  setPlanOptions(options: PlanOption[]): void;
  /** The shared email-conflict verdict; the PayPro rail asks the same question. */
  classifyCheckoutEmailAsync(email: string): Promise<CheckoutEmailVerdict>;
}

/** The services the rail calls out to. The page injects them and hands them over. */
export interface PaddleRailServices {
  auth: AuthService;
  logging: LoggingService;
  settings: SettingsService;
  alertCtrl: AlertController;
  navCtrl: NavController;
  cloudFunctions: CloudFunctionsService;
  paddleCheckout: PaddleCheckoutService;
  urlQuery: UrlQueryService;
}

// turn one Paddle price + its previewed total into the PlanOption shape the
// shared selector renders. `buyUrl` is unused for Paddle (the inline frame
// replaces the iframe), so it is left empty.
function buildPaddlePlan(title: string, priceId: string, numMonths: number, priceById: Map<string, PaddlePlanPrice>): PlanOption {
  const preview = priceById.get(priceId);
  const parsed = preview ? parsePrice(preview.formattedTotal) : undefined;
  return {
    title,
    name: `Paddle ${title}`,
    id: priceId,
    buyUrl: '',
    price: parsed?.value,
    currency: preview?.currency,
    priceText: preview?.formattedTotal,
    numMonths,
    trialPeriod: preview?.trialPeriod,
  };
}

/**
 * The Paddle inline-checkout rail of the subscribe page, split out of the page
 * class (#3861 built it; this file is the deferred-findings entry
 * `df-f3f664504bca` carried out for one of that page's three payment rails).
 *
 * ## Why this is a plain class and not an Angular service
 *
 * Everything here is state belonging to ONE visit to the subscribe page — which
 * plan's frame is open, whether payment has completed, the transaction id
 * Continue signs a purchase-first buyer in with. An `@Injectable()` service is a
 * singleton, so that state would survive the page and leak into the next visit.
 * The page constructs one of these and drops it with itself.
 *
 * ## What it does NOT own
 *
 * The page keeps everything the other two payment rails also touch: which web
 * rail is active (`webProvider`), the plan list the shared selector renders, the
 * checkout spinner, and the email-conflict check both web rails run. The rail
 * reaches those through {@link PaddleRailHost} rather than owning them, because
 * owning them would mean the PayPro rail had to go through Paddle's object to
 * reach its own state.
 *
 * The other two rails have since moved out too, into `subscribe-paypro-rail.ts`
 * and `subscribe-native-rail.ts`, so the page now holds none of the three inline.
 * The Paddle glue this rail shares with `coach-checkout-page.ts` — the frame class,
 * the frame-ready poll and the event routing — lives in `paddle-inline-frame.ts`;
 * see `design-docs/paddle-integration.md`.
 */
export class SubscribePaddleRail {
  private config?: PaddleCheckoutConfig;
  // true once the inline checkout has been opened; a later plan switch then
  // updates its items instead of re-opening. Reset on teardown.
  private checkoutOpened = false;
  // true once Paddle's CHECKOUT_COMPLETED has fired (payment succeeded). drives
  // the template to show Paddle's success screen plus our Continue button instead
  // of auto-reloading out from under the success screen.
  private paymentComplete = false;
  // true while we poll for the grant after the buyer taps Continue, so a double
  // tap can't start a second poll loop.
  private finalizing = false;
  // the Paddle transaction id from CHECKOUT_COMPLETED. for a purchase-first buyer
  // (no session yet) this is how Continue signs them in: the server resolves the
  // account the webhook stamped with this id and mints a claim token to exchange.
  private purchaseTransactionId: string | undefined;

  constructor(
    private readonly services: PaddleRailServices,
    private readonly host: PaddleRailHost
  ) {}

  /** True once payment succeeded, so the page can show its Continue button. */
  get purchaseComplete(): boolean {
    return this.paymentComplete;
  }

  // init Paddle.js with the server-resolved env + token, then build the two-plan
  // selector from a localized PricePreview (falls back to title-only plans).
  async setupAsync(config: PaddleCheckoutConfig | undefined): Promise<void> {
    this.config = config;
    if (!this.config) {
      // the provider is a literal rather than read back from the page: the page
      // only calls this once it has resolved the web rail to Paddle, so this
      // report carried 'paddle' before the rail moved out of the page too.
      this.services.logging.trackSilentProblem('PaddleConfigMissing', { provider: 'paddle' });
      return;
    }
    await this.services.paddleCheckout.initAsync({
      environment: this.config.environment,
      token: this.config.clientToken,
      eventCallback: (ev) => this.onPaddleEvent(ev),
    });
    const priceIds = [this.config.priceMonthly, this.config.priceYearly];
    const prices = await this.services.paddleCheckout.previewPricesAsync(priceIds, this.services.settings.locale.localeName);
    const priceById = new Map(prices.map(p => [p.priceId, p]));
    const monthly = buildPaddlePlan('Monthly', this.config.priceMonthly, 1, priceById);
    const yearly = buildPaddlePlan('Yearly', this.config.priceYearly, 12, priceById);
    this.addYearlySaving(yearly, priceById.get(this.config.priceMonthly), priceById.get(this.config.priceYearly));
    this.host.setPlanOptions([monthly, yearly]);
  }

  // #6145: the crossed-out 12 x Monthly price and the green "Save" badge on the Yearly row,
  // which the PayPro rail and the store apps already show.
  private addYearlySaving(yearly: PlanOption, monthlyPrice: PaddlePlanPrice | undefined, yearlyPrice: PaddlePlanPrice | undefined): void {
    // if the preview failed, the plans carry no price at all, and that failure was already reported
    if (!monthlyPrice || !yearlyPrice) return;
    const saving = computeYearlySaving({
      monthlyMinor: monthlyPrice.totalMinor,
      yearlyMinor: yearlyPrice.totalMinor,
      currency: yearlyPrice.currency,
      yearlyPriceText: yearlyPrice.formattedTotal,
    });
    if (!saving) {
      // the prices are on screen but the saving could not be worked out safely, so the row
      // shows the price alone. Only the currency goes in the report, never a price.
      this.services.logging.trackSilentProblem('PaddleYearlySavingUnrenderable', { currency: yearlyPrice.currency });
      return;
    }
    yearly.preDiscount = saving.preDiscount;
    yearly.saving = saving.saving;
  }

  // first selection opens the inline checkout; a later plan switch updates the
  // items on the already-open frame (re-`open`-ing is not the documented way and
  // can leave a stale frame). Only the first open shows the loading spinner — a
  // switch keeps the frame visible, and CHECKOUT_LOADED may not re-fire.
  selectPlan(opt: PlanOption): void {
    // a sandbox checkout that is NOT marked e2e can never grant a license (the
    // webhook sandbox grant-gate refuses it — decision 3). Don't render a
    // checkout the buyer could complete but never be granted from; leave the
    // frame empty. This only ever happens off-prod (prod only returns a sandbox
    // config for an already-marked e2e request), i.e. the dev non-`e2e-test`
    // case. To enable the sandbox test checkout on dev, load /subscribe with the
    // e2e-test marker via a FULL page reload (the marker is captured once at app
    // bootstrap). The log line is deliberately generic — it must NOT advertise
    // the marker, since that would hand bots/fraud a recipe to flip into the
    // (license-granting) sandbox test rail.
    if (this.config?.environment === 'sandbox' && !this.config.isE2e) {
      console.warn('Paddle: inline checkout not available for this request.');
      this.host.setLoadingCheckout(false);
      return;
    }
    if (this.checkoutOpened) {
      if (this.services.paddleCheckout.isInitialized) {
        this.services.paddleCheckout.updateItems(opt.id);
      }
      return;
    }
    this.host.setLoadingCheckout(true);
    this.openCheckout(opt);
  }

  /**
   * Close an already-open frame so the NEXT open stamps fresh `custom_data`.
   *
   * Used when a referral claim has re-requested the checkout (#4962). Deliberately
   * NOT {@link teardown}: the buyer is staying on the page, so the post-purchase
   * flags must keep whatever they hold.
   */
  closeBeforeReopen(): void {
    this.services.paddleCheckout.close();
    this.checkoutOpened = false;
  }

  /**
   * Leaving the page: close the inline checkout so it does not linger across
   * navigation (the eventCallback closure captures the now-stale page).
   *
   * Resetting the post-purchase flags is part of this. Without it, a buyer who
   * taps Continue (`finalizing` = true) and then leaves mid-poll WITHOUT a reload
   * (e.g. menu navigation) would find Continue permanently dead on return, since
   * `onContinueAsync` early-returns while `finalizing` stays true.
   */
  teardown(): void {
    this.services.paddleCheckout.close();
    this.checkoutOpened = false;
    this.finalizing = false;
    this.paymentComplete = false;
    this.purchaseTransactionId = undefined;
  }

  // open the Paddle inline checkout for the selected plan. Stamps custom_data
  // (origin_host + checkout_id, plus app_user_id when logged in and is_e2e for a
  // test run) so the webhook can route + identify + grant. A logged-in buyer's
  // email is pre-filled and locked (allowLogout:false); a purchase-first buyer
  // types it in the frame and we watch checkout.customer.updated for conflicts.
  private openCheckout(opt: PlanOption): void {
    if (!this.config || !this.services.paddleCheckout.isInitialized) {
      this.services.logging.trackSilentProblem('PaddleOpenBeforeInit', { priceId: opt.id });
      return;
    }
    // the inline frame mounts into a div Angular renders only once webProvider
    // is 'paddle'. The first open fires right after setup, before the next
    // change-detection flush, so wait for the target element to exist.
    openWhenPaddleFrameReady({
      schedule: (callback, delayMs) => this.host.scheduleOnPage(callback, delayMs),
      onFrameReady: () => this.openIntoFrame(opt),
      onGiveUp: () => {
        this.services.logging.trackSilentProblem('PaddleFrameTargetMissing', { priceId: opt.id });
        void this.offerCheckoutRetryAsync(opt);
      },
    });
  }

  // the frame container never rendered: clear the spinner so the buyer is not left
  // on a dead overlay, and offer another attempt. Unlike the coach checkout this does
  // NOT go back — for a locked-out user this page is often the nav root, and popping
  // it would walk out of the app. Cancel just leaves the page usable; picking a plan
  // again reopens the checkout, since checkoutOpened is still false.
  private async offerCheckoutRetryAsync(opt: PlanOption): Promise<void> {
    // if the buyer already left, there is nothing to clear or offer
    if (this.host.isViewGone()) return;
    this.host.setLoadingCheckout(false);
    this.host.markPageForCheck();
    const retry = await this.services.alertCtrl.showAlertAsync({
      title: LL.subscribe.alerts.checkoutUnavailable.title(),
      message: LL.subscribe.alerts.checkoutUnavailable.message(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.tryAgain(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    // if the page went away while the alert was open, or a plan pick already opened
    // the checkout meanwhile, do nothing more
    if (!retry || this.host.isViewGone() || this.checkoutOpened) return;
    this.host.setLoadingCheckout(true);
    this.host.markPageForCheck();
    this.openCheckout(opt);
  }

  // the frame container exists: stamp custom_data and open the checkout into it.
  private openIntoFrame(opt: PlanOption): void {
    const customData: Record<string, unknown> = {
      origin_host: location.host,
      checkout_id: this.config.checkoutId,
      // tag the purchase with the language the app is showing, so the webhook can
      // write the welcome email in it (#4244). The buyer may not be signed in and
      // the account may not exist yet, so this is the only moment the app's
      // language and the eventual purchase meet.
      locale: BOOT_LOCALE,
      // the app's tracking data (utm_*, gclid, fbclid + its click time, referrer,
      // user agent) plus the buyer's IP the server saw when it built this config.
      // This is the Paddle rail's equivalent of PayPro's `x-data`, and the only
      // way any of it reaches the webhook — which arrives from Paddle's servers,
      // for an account that on a purchase-first checkout does not exist yet
      // (#4045, #4257).
      // the second argument is what only the SERVER could see: a `Sec-GPC: 1`
      // header on the request that built this config (#5663). Some browsers and
      // extensions send the header without setting the page-visible property,
      // so the page cannot detect its own signal.
      tracking: this.services.urlQuery.getTrackingDataForCheckout(
        this.config.clientIp, this.config.gpcDetected === true),
    };
    if (this.services.auth.user?.uid) {
      customData.app_user_id = this.services.auth.user.uid;
    }
    if (this.config.isE2e) {
      customData.is_e2e = true;
    }
    try {
      this.services.paddleCheckout.openInlineCheckout({
        priceId: opt.id,
        // bare class name (not a selector) — Paddle mounts via getElementsByClassName.
        frameTarget: kPaddleFrameClass,
        customData,
        customerEmail: this.services.auth.user?.email || undefined,
        // lock the email when logged in; let a purchase-first buyer type it.
        allowLogout: !this.services.auth.user,
        // deliberately NO successUrl: for an inline checkout a successUrl makes
        // Paddle redirect the top window on completion, which tears this page down
        // before the CHECKOUT_COMPLETED handler can run (and pointing it back at
        // /subscribe just reloads the buy page anyway). Instead we stay on the
        // page, let onPaddleEvent's CHECKOUT_COMPLETED fire, and route the buyer
        // off /subscribe ourselves in onContinueAsync once the buyer taps
        // Continue and the grant lands.
      });
      // mark open so a later plan switch updates items instead of re-opening.
      this.checkoutOpened = true;
    } catch (err) {
      // Paddle.Checkout.open can throw synchronously (e.g. a bad frameTarget):
      // clear the spinner and surface, so the buyer never stares at a frozen frame.
      this.host.setLoadingCheckout(false);
      this.host.markPageForCheck();
      this.services.logging.trackAbnormalException(err, LL.subscribe.errors.unexpectedCheckout());
    }
  }

  // report one Paddle checkout error at the tier its kind deserves (df-38f32a018053).
  // it runs even after the page is gone, so it must not touch the view.
  private reportPaddleCheckoutError(errorEvent: PaddleEventData, paddleDetail: string, kind: PaddleCheckoutErrorKind): void {
    // capture Paddle's own fields so the reason is visible instead of a
    // generic "something went wrong".
    console.error(`Paddle ${errorEvent.name}: type=${errorEvent.type} code=${errorEvent.code} detail=${errorEvent.detail} docs=${errorEvent.documentation_url}`);
    switch (kind) {
      case PaddleCheckoutErrorKind.Network:
        // the request never reached Paddle (offline, ad blocker): not our fault, and
        // Paddle's frame already shows its own error, so no toast (df-38f32a018053)
        this.services.logging.trackSilentInfo(kPaddleCheckoutNetworkErrorReport, paddleCheckoutErrorContext(errorEvent, PaddleCheckoutPage.Subscribe));
        break;
      case PaddleCheckoutErrorKind.NoFields:
        // cause unknown: keep it a problem, and record whether the price preview had
        // already failed for the network, so the next event says which case it is
        this.services.logging.trackSilentProblem(kPaddleCheckoutFailedNoFieldsReport, {
          page: PaddleCheckoutPage.Subscribe,
          event: errorEvent.name,
          lastPricePreview: this.services.paddleCheckout.lastPricePreview,
        });
        break;
      default:
        this.services.logging.trackAbnormalException(
          new Error(`Paddle checkout ${errorEvent.name}: ${paddleDetail}`),
          LL.subscribe.errors.unexpectedCheckout(),
        );
        break;
    }
  }

  // Paddle.js event callback. Mirrors the PPG postMessage handler: track the
  // typed email for the conflict check, drop the loading spinner once the frame
  // loads, and surface the Continue button on completion. Entitlement still comes
  // from the webhook; CHECKOUT_COMPLETED only flips us to the success-screen state
  // here (the grant is verified later, when the buyer taps Continue).
  private onPaddleEvent(ev: PaddleEventData): void {
    // the ordering rule — an error is reported even on a torn-down page, every
    // other event is dropped there — lives in dispatchPaddleCheckoutEvent.
    dispatchPaddleCheckoutEvent(ev, {
      isViewGone: () => this.host.isViewGone(),
      reportCheckoutError: (errorEvent, paddleDetail, kind) => this.reportPaddleCheckoutError(errorEvent, paddleDetail, kind),
      stopSpinner: () => {
        this.host.setLoadingCheckout(false);
        this.host.markPageForCheck();
      },
      onCustomerEmail: (email) => {
        void this.handleCustomerEmailAsync(email);
      },
      onCompleted: (completedEvent) => {
        // payment succeeded (this is Paddle's client-side notification; the
        // license grant still arrives out-of-band via the webhook a few seconds
        // later). Paddle now shows its success screen in the inline frame — we
        // surface our Continue button beside it and do NOT reload yet. the buyer
        // reads the success, taps Continue, and only then do we verify the grant
        // and reload (see onContinueAsync). this keeps us from killing the
        // success screen the moment the grant lands.
        console.log('Paddle: checkout completed');
        // capture the transaction id — a purchase-first buyer has no session, so
        // this is the only handle Continue has to resolve and sign into the
        // account the webhook creates (see onContinueAsync).
        this.purchaseTransactionId = completedEvent.data?.transaction_id;
        this.paymentComplete = true;
        this.host.markPageForCheck();
      },
    });
  }

  // Invoked when the buyer taps Continue on the Paddle success screen. The
  // license grant is delivered out-of-band by Paddle's webhook a few seconds
  // after payment, and is only visible to the client through a server read (it
  // lands in the _users auth DB, not in synced data). We detect it with the
  // lightweight getSubscriptionInfo read — a plain server call that does NOT
  // re-login or re-init databases, so it does not churn the app or tear this page
  // down (a full couchLogin re-login here would re-emit auth state, re-navigate,
  // and destroy this page before it could reload). Once the subscription reads
  // Active we cache the paid role locally so the fresh boot routes to Home, then
  // do a full reload to clear any lingering unpaid in-memory state and bring the
  // app up cleanly under the paid role.
  async onContinueAsync(): Promise<void> {
    // if Continue is tapped twice, do not start a second poll loop
    if (this.finalizing) {
      return;
    }
    this.finalizing = true;

    // a purchase-first buyer has no session yet, so getSubscriptionInfo (which
    // needs a uid) has nothing to poll. instead sign them into the account the
    // webhook is creating, using the Paddle transaction id as the handle. this is
    // PPG's claim-token sign-in, fetched here on Continue because the inline flow
    // has no completion redirect to carry the token.
    if (!this.services.auth.user) {
      await this.finalizePurchaseFirstSignInAsync();
      return;
    }

    // now that the buyer has tapped Continue, show the spinner while we confirm
    // the grant and reload (the success screen's job is done at this point)
    this.host.setLoadingCheckout(true);
    this.host.markPageForCheck();

    // poll the lightweight subscription-info read until the webhook grant has
    // flipped the subscription to Active. the webhook usually lands within a few
    // seconds; the wait allows a generous window for a slow cluster. how long that
    // is, and why, lives in post-purchase-wait-policy.ts rather than here — three
    // flows wait for a webhook and their budgets used to drift apart (#4488).
    // remember the last server-reported error so a PERSISTENT one can surface once
    // at timeout. we do NOT log it per attempt: a user-not-found error while the
    // webhook is still in flight is the normal race this wait sits through, so
    // per-attempt logging would spam Sentry with ~30 expected events per purchase.
    let lastStatusError: string | undefined;
    const granted = await pollForPostPurchaseAsync<GetSubscriptionInfoResult>(kAlreadySignedInWaitBudget, {
      isCancelled: () => this.host.isViewGone(),
      attemptAsync: async () => {
        // read the current subscription status without re-logging-in
        let info: GetSubscriptionInfoResult | undefined;
        try {
          info = await this.services.cloudFunctions.getSubscriptionInfoAsync({ platform: 'web' });
        } catch (err) {
          // a transient failure of the status probe is not terminal — log it and
          // keep polling; the grant may still arrive on a later attempt.
          this.services.logging.trackSilentProblem('PaddleFinalizeStatusProbeFailed', { message: (err as Error)?.message });
        }
        if (this.host.isViewGone()) return undefined;
        // the read can RESOLVE with an error field (not throw); stash it for the
        // timeout path rather than logging every attempt.
        if (info?.error) {
          lastStatusError = info.error;
        }
        // a status short of Active means keep waiting — the grant has not landed yet
        return info?.subscriptionStatus === SubscriptionStatus.Active ? info : undefined;
      },
    });
    if (granted.cancelled) return;
    if (granted.result) {
      // the grant landed. cache the paid role locally BEFORE the reload, so the
      // fresh boot reads the paid role from localStorage and routes the buyer to
      // Home — instead of racing ahead with a not-yet-loaded role and landing
      // back on /subscribe.
      this.services.auth.cacheLicenseActivation({ role: AuthRole.Paid, licenseExpires_utc: granted.result.licenseExpires_utc, error: '' });
      // cacheLicenseActivation's own save is conditional and fire-and-forget;
      // await an explicit save here to GUARANTEE the cached paid role is
      // persisted to localStorage before the reload reads it on boot.
      await this.services.settings.saveAsync();
      if (this.host.isViewGone()) return;
      // full reload clears any lingering unpaid-state in memory and brings the
      // app up cleanly under the paid role.
      void getMyApp().reloadAppAsync({ resetToRoot: true });
      return;
    }

    // the grant did not arrive in time. if the server kept reporting an error
    // across the whole wait, surface it ONCE now — that is the genuinely
    // unexpected, persistent case (vs the transient in-flight race above).
    if (lastStatusError) {
      this.services.logging.trackSilentProblem('PaddleFinalizeStatusError', { error: lastStatusError });
    }

    await this.showRegistrationDelayedAndReloadAsync();
  }

  // Sign a purchase-first buyer in after they tap Continue. They have no session;
  // the webhook is creating their account and stamping paddleData.transactionId.
  // We poll the server for a purchase claim token keyed on that transaction id,
  // then exchange it for a session — the SAME claim-token path PPG uses on boot,
  // just fetched here because the inline flow has no completion redirect to carry
  // the token. On timeout we fall back to a full reload, which lands on the
  // logged-out subscribe state with the "check your email for a sign-in link"
  // affordance (the webhook also emailed that link).
  private async finalizePurchaseFirstSignInAsync(): Promise<void> {
    // now that Continue was tapped, show the spinner while we sign in (the
    // success screen's job is done at this point).
    this.host.setLoadingCheckout(true);
    this.host.markPageForCheck();

    const transactionId = this.purchaseTransactionId;
    // without a transaction id there is no handle to resolve the new account.
    // surface it (it should always be set on CHECKOUT_COMPLETED) and fall back to
    // the email-link affordance.
    if (!transactionId) {
      this.services.logging.trackSilentProblem('PaddlePurchaseFirstNoTransactionId', {});
      void getMyApp().reloadAppAsync({ resetToRoot: true });
      return;
    }

    // poll for the claim token: the webhook lands within a few seconds and stamps
    // paddleData.transactionId, after which the server can mint the token. a
    // 'not_found' result is the expected in-flight race — keep polling.
    // two different endings drop the buyer into the SAME "registration delayed" alert
    // below: the claim token never appeared (the wait ran out of attempts), or a token
    // was minted but exchanging it for a session failed. They send a reader in different
    // directions, so the report at the bottom must say which one happened — see #4488,
    // where this whole path reported nothing at all. That is why the attempt below
    // answers with WHICH ending it hit rather than with the token: both endings stop the
    // wait, and only one of them is the buyer getting in.
    const signIn = await pollForPostPurchaseAsync(kPurchaseFirstWaitBudget, {
      isCancelled: () => this.host.isViewGone(),
      attemptAsync: async () => {
        let claimToken: string | undefined;
        try {
          const res = await this.services.cloudFunctions.getPaddlePurchaseClaimTokenAsync({ transactionId });
          claimToken = res?.claimToken;
        } catch (err) {
          // a transient failure of the token fetch is not terminal — log and keep
          // polling; the account may resolve on a later attempt.
          this.services.logging.trackSilentProblem('PaddlePurchaseFirstClaimFetchFailed', { message: (err as Error)?.message });
        }
        if (this.host.isViewGone()) return undefined;
        // no token yet is the expected in-flight race — keep waiting
        if (!claimToken) return undefined;
        // exchange the claim token for a Firebase session — the same call the
        // boot path makes for a PPG claim token (app.component exchangeClaimToken).
        try {
          const exchange = await this.services.cloudFunctions.exchangePurchaseClaimTokenAsync({ claimToken });
          if (this.host.isViewGone()) return undefined;
          if (exchange?.token) {
            await this.services.auth.signInWithTokenAsync(exchange.token);
            if (this.host.isViewGone()) return undefined;
            return PurchaseFirstSignInEnding.SignedIn;
          }
          // a resolved-but-errored exchange (expired / user_not_found) is
          // genuinely unexpected here — the token was just minted — so surface it.
          if (exchange?.error) {
            this.services.logging.trackSilentProblem('PaddlePurchaseFirstExchangeError', { error: exchange.error });
          }
        } catch (err) {
          this.services.logging.trackAbnormalException(err, LL.subscribe.errors.unexpectedCheckout());
        }
        // exchange did not yield a session — stop waiting and fall through to the
        // recovery alert (re-fetching the same token would not help).
        return PurchaseFirstSignInEnding.ExchangeFailed;
      },
    });
    // the failed-exchange ending stops the wait without asking again, so ask here: the
    // buyer may have left while that exchange was in flight, and nobody is owed an alert.
    if (signIn.cancelled || this.host.isViewGone()) return;
    if (signIn.result === PurchaseFirstSignInEnding.SignedIn) {
      // #6788: signing in has started creating the buyer's brand-new local store on THIS
      // page. Reloading straight away cut that first open off, and on two iPhones the store
      // then stayed silent on the next start. Let the open finish first, capped so a slow
      // phone reloads as it did before.
      // no isViewGone check after it: the cold start that signing in began may itself move
      // off this page while we wait, and the reload must still happen exactly as before
      await getMyApp().whenUserDbInitSettledAsync(kPurchaseReloadStoreWaitMs);
      // label the next start's reports, so a silent store after this reload can be told apart
      markAfterPurchaseReload(typeof sessionStorage !== 'undefined' ? sessionStorage : null);
      // full reload brings the app up cleanly under the now-signed-in,
      // paid session and routes the buyer to Home.
      void getMyApp().reloadAppAsync({ resetToRoot: true });
      return;
    }

    // report the give-up. This path had NO telemetry at all until #4488, so how often
    // a real buyer is told their payment registration was delayed — after paying us —
    // was simply unknown, and a search for it in Sentry came back empty in a way that
    // looked like good news. `trackSilentProblem` is the right tier: the condition is
    // unresolved, but the buyer is not interrupted by the report — the alert they do see
    // is recovery guidance, not this. The sibling already-signed-in loop reports
    // 'PaddleFinalizeStatusError' at its own timeout for the same reason.
    this.services.logging.trackSilentProblem('PaddlePurchaseFirstTimeout', {
      // which of the two endings happened (see the enum's declaration)
      ending: signIn.result === PurchaseFirstSignInEnding.ExchangeFailed ? 'claim-token-exchange-failed' : 'claim-token-never-arrived',
      attemptsMade: signIn.attemptsMade,
      // ask the policy module for the elapsed wait rather than re-deriving its
      // "n attempts sleep n-1 times" rule here — one owner for that arithmetic is
      // the whole point of the module, and the wait itself now lives there too.
      waitedMs: totalWaitMs({ pollIntervalMs: kPurchaseFirstWaitBudget.pollIntervalMs, maxAttempts: signIn.attemptsMade }),
      maxAttempts: kPurchaseFirstWaitBudget.maxAttempts,
    });

    await this.showRegistrationDelayedAndReloadAsync();
  }

  /**
   * The give-up ending both post-purchase waits share.
   *
   * Do not leave the buyer staring at the checkout: drop the spinner, let Continue
   * work again, and surface the "registration delayed" alert (its Retry reloads) so
   * the situation reaches them and they can recover. Then reload regardless of which
   * button they used — the alert has only the one.
   *
   * Written once because the two waits ended in byte-identical code. What they do
   * BEFORE this differs (one polls the subscription status, the other polls for a
   * claim token and exchanges it) and each reports its own distinct timeout problem,
   * so only this tail is shared.
   */
  private async showRegistrationDelayedAndReloadAsync(): Promise<void> {
    this.host.setLoadingCheckout(false);
    this.finalizing = false;
    this.host.markPageForCheck();
    await this.services.alertCtrl.showAlertAsync({
      title: LL.subscribe.alerts.registrationSlow.title(),
      message: LL.subscribe.alerts.registrationSlow.message(),
      buttons: [{ text: LL.fatalError.buttons.retry(), role: AlertButtonRole.Primary, result: null }],
    });
    if (this.host.isViewGone()) return;
    void getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  // when a purchase-first buyer types an email Paddle reports, run the same
  // conflict check we do for PPG. On a conflict we tear the inline checkout down
  // (close()) and divert, since Paddle has no withhold-the-handshake gate; the
  // webhook duplicate/identity guard is the authoritative backstop for the race.
  private async handleCustomerEmailAsync(email?: string): Promise<void> {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }
    const verdict = await this.host.classifyCheckoutEmailAsync(email);
    if (verdict === 'ok') {
      return; // no conflict — let the Paddle checkout proceed
    }
    // a conflict (or a check failure): tear down the inline checkout first.
    this.services.paddleCheckout.close();
    if (verdict === 'exists') {
      await this.services.alertCtrl.showAlertAsync({
        title: LL.subscribe.alerts.accountExists.title(),
        message: LL.subscribe.alerts.accountExists.message(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      void this.services.navCtrl.setRootAsync(ILoginPage, { mode: 'login', email, autoSendLink: true });
    } else if (verdict === 'mismatch') {
      await this.services.alertCtrl.showAlertAsync({
        title: LL.subscribe.alerts.emailMismatch.title(),
        message: LL.subscribe.alerts.emailMismatch.message(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      void getMyApp().reloadAppAsync({ resetToRoot: false });
    } else {
      await this.services.alertCtrl.showAlertAsync({
        title: LL.subscribe.alerts.cantVerifyEmail.title(),
        messageMarkdown: LL.subscribe.alerts.cantVerifyEmail.message(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      });
      void getMyApp().reloadAppAsync({ resetToRoot: false });
    }
  }
}
